import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import { Loader2, ArrowRight, MapPin, CheckCircle2 } from "lucide-react";

const HOOKUPS = [
  { id: "toilet", label: "Portable Toilet ($160/mo)" },
  { id: "container", label: "40 ft Container ($399/mo)" },
  { id: "power", label: "3-Phase Power Supply" },
  { id: "water", label: "Water Hookup" },
  { id: "lights", label: "Custom Lighting" },
];

const TIERS = [
  { sqft: 5000, price: 950 },
  { sqft: 10000, price: 1850 },
  { sqft: 15000, price: 2590 },
];

export default function Contact() {
  const [params] = useSearchParams();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    desired_sq_ft: parseInt(params.get("size") || "5000"),
    hookups: [],
    notes: "",
  });

  useEffect(() => {
    const size = params.get("size");
    if (size) setForm((f) => ({ ...f, desired_sq_ft: parseInt(size) }));
  }, [params]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleHookup = (id) => setForm((f) => ({
    ...f, hookups: f.hookups.includes(id) ? f.hookups.filter((x) => x !== id) : [...f.hookups, id],
  }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", {
        ...form,
        desired_sq_ft: parseInt(form.desired_sq_ft || 0),
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="contact-page" className="min-h-screen text-[#2C2F36]">
      <Navbar />

      <section className="py-16 lg:py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="overline text-[#0B0C0E]"><MapPin className="inline w-3 h-3 mr-1" /> 14 ACRES · EDMONTON · ALBERTA</div>
          <h1 className="mt-4 font-display font-black uppercase tracking-tighter text-5xl sm:text-6xl lg:text-7xl text-[#0B0C0E] leading-[0.92]">
            Need a custom<br/>
            <span className="bg-[#FACC15] px-3 inline-block">lot size?</span>
          </h1>
          <p className="mt-6 text-lg text-[#2C2F36] max-w-2xl">
            Tell us what you need. We sub-divide our 14 acres to suit — starting from 5,000 sq ft. We&apos;ll reply within one business day with a quote.
          </p>

          {/* Pricing tiers */}
          <div className="mt-12 grid sm:grid-cols-3 gap-px bg-[#0B0C0E] border-2 border-[#0B0C0E]" data-testid="pricing-tiers">
            {TIERS.map((t) => (
              <button
                key={t.sqft}
                onClick={() => setField("desired_sq_ft", t.sqft)}
                className={`p-6 text-left transition ${form.desired_sq_ft === t.sqft ? "bg-[#FACC15]" : "bg-white hover:bg-[#FCFBF6]"}`}
                data-testid={`tier-${t.sqft}`}
              >
                <div className="overline">Reference Tier</div>
                <div className="mt-2 font-display font-black text-3xl text-[#0B0C0E]">{t.sqft.toLocaleString()} <span className="text-base font-medium text-[#56606E]">sq ft</span></div>
                <div className="mt-3 font-display font-black text-2xl text-[#0B0C0E]">{fmtMoney(t.price)}<span className="text-sm font-medium text-[#56606E]">/mo + GST</span></div>
              </button>
            ))}
          </div>
          <p className="mt-3 text-xs text-[#56606E]">Click a tile to pre-fill the form. Final pricing depends on hookups, term, and location on the plan.</p>

          {/* Form */}
          {submitted ? (
            <div className="mt-12 border-2 border-emerald-600 bg-emerald-50 p-8 flex items-start gap-4" data-testid="submitted-banner">
              <CheckCircle2 className="w-8 h-8 text-emerald-700 flex-shrink-0" />
              <div>
                <h3 className="font-display font-black text-2xl uppercase tracking-tight text-[#0B0C0E]">Request received</h3>
                <p className="mt-2 text-sm text-[#2C2F36]">Thanks {form.name.split(" ")[0]}, we&apos;ve logged your request for a {form.desired_sq_ft.toLocaleString()} sq ft lot. We&apos;ll be in touch at <strong>{form.email}</strong> within one business day.</p>
                <a href="/" className="mt-6 inline-flex items-center gap-2 border-2 border-[#0B0C0E] hover:bg-[#FACC15] text-[#0B0C0E] px-6 py-2 text-xs uppercase font-bold">Back to home</a>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-12 border-2 border-[#0B0C0E] bg-white p-8" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full Name *" required value={form.name} onChange={(v) => setField("name", v)} testId="contact-name" />
                <Field label="Company (optional)" value={form.company} onChange={(v) => setField("company", v)} testId="contact-company" />
                <Field label="Email *" type="email" required value={form.email} onChange={(v) => setField("email", v)} testId="contact-email" />
                <Field label="Phone *" type="tel" required value={form.phone} onChange={(v) => setField("phone", v)} testId="contact-phone" />
                <Field label="Desired Size (sq ft) *" type="number" required value={form.desired_sq_ft} onChange={(v) => setField("desired_sq_ft", v)} testId="contact-size" />
              </div>

              <div className="mt-6">
                <label className="overline mb-2 block">Hookups needed (optional)</label>
                <div className="grid sm:grid-cols-3 gap-2">
                  {HOOKUPS.map((h) => (
                    <label key={h.id} className={`flex items-center gap-2 border px-3 py-2 cursor-pointer text-sm ${form.hookups.includes(h.id) ? "border-[#0B0C0E] bg-[#FACC15]/30 font-bold" : "border-[#C6C3B5] bg-white"}`} data-testid={`hookup-${h.id}`}>
                      <input type="checkbox" checked={form.hookups.includes(h.id)} onChange={() => toggleHookup(h.id)} className="accent-[#0B0C0E]" />
                      {h.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="mt-6">
                <label className="overline mb-2 block">Notes</label>
                <textarea rows={4} value={form.notes} onChange={(e) => setField("notes", e.target.value)}
                  placeholder="Tell us about your equipment, timeline, term length, etc."
                  className="w-full bg-white border border-[#C6C3B5] focus:border-[#0B0C0E] outline-none px-3 py-2 text-sm text-[#0B0C0E]"
                  data-testid="contact-notes" />
              </div>

              <button type="submit" disabled={loading}
                className="mt-8 bg-[#0B0C0E] hover:bg-black disabled:opacity-50 text-white font-bold px-10 py-4 uppercase tracking-wide inline-flex items-center gap-3 border-l-4 border-[#FACC15]"
                data-testid="contact-submit">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Send Request <ArrowRight className="w-4 h-4" /></>}
              </button>
              <p className="mt-3 text-xs text-[#56606E]">By submitting you agree to be contacted at the email and phone provided.</p>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", required, testId }) => (
  <div>
    <label className="overline mb-2 block">{label}</label>
    <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full bg-white border border-[#C6C3B5] focus:border-[#0B0C0E] outline-none px-4 py-3 text-[#0B0C0E] text-sm"
      data-testid={testId} />
  </div>
);
